import { useState } from "react";
import { useUserStats } from "../lib/UserStatsContext";
import { DIET_OPTIONS, ALLERGY_OPTIONS } from "../lib/userStats";
import type { DietType } from "../lib/userStats";
import { loadNudgePreferences, saveNudgePreferences, type NudgePreferences } from "../lib/nudges";
import { getGLTarget, setGLTarget } from "../lib/energyTracker";

export default function ProfilePreferencesTab() {
  const { stats, updatePrefs } = useUserStats();
  const [dietType, setDietType] = useState<DietType>(stats?.dietType || "none");
  const [allergies, setAllergies] = useState<string[]>(stats?.allergies || []);
  const [saving, setSaving] = useState(false);
  const [nudgePrefs, setNudgePrefs] = useState<NudgePreferences>(loadNudgePreferences);
  const [nudgeSaved, setNudgeSaved] = useState(false);
  const [glDailyTarget, setGlDailyTarget] = useState(getGLTarget);

  const hasUnsavedChanges =
    stats && (dietType !== stats.dietType || JSON.stringify(allergies.sort()) !== JSON.stringify((stats.allergies || []).sort()));

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await updatePrefs(dietType, allergies);
    } catch (err) {
      console.error("Failed to save preferences:", err);
    } finally {
      setSaving(false);
    }
  };

  const toggleAllergy = (allergyId: string) => {
    setAllergies((prev) =>
      prev.includes(allergyId)
        ? prev.filter((a) => a !== allergyId)
        : [...prev, allergyId]
    );
  };

  const updateNudgePref = (key: keyof NudgePreferences, value: boolean) => {
    const newPrefs = { ...nudgePrefs, [key]: value };
    setNudgePrefs(newPrefs);
    saveNudgePreferences(newPrefs);
    setNudgeSaved(true);
    setTimeout(() => setNudgeSaved(false), 1500);
  };

  return (
    <>
      {/* Dietary Preferences */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="section-label">Dietary Preferences</div>
          {hasUnsavedChanges && (
            <button
              onClick={handleSavePreferences}
              disabled={saving}
              className="px-4 py-1.5 bg-accent text-white rounded-md font-[family-name:--font-ui] text-sm font-medium hover:opacity-85 active:opacity-75 disabled:opacity-50 -mt-1"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
        </div>
        <p className="text-sm text-muted mb-4">
          Your preferences help personalize recommendations across all features.
        </p>

        {/* Diet Type */}
        <div className="mb-6">
          <p className="text-sm text-muted font-[family-name:--font-ui] mb-3">Diet type</p>
          <div className="flex flex-wrap gap-2">
            {DIET_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => setDietType(option.id)}
                className={`rounded-md px-3 py-2 text-sm font-[family-name:--font-ui] font-medium transition-colors ${
                  dietType === option.id
                    ? "bg-accent text-white"
                    : "border border-border text-text hover:bg-black/5"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Allergies */}
        <div>
          <p className="text-sm text-muted font-[family-name:--font-ui] mb-3">Allergies & intolerances</p>
          <div className="flex flex-wrap gap-2">
            {ALLERGY_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => toggleAllergy(option.id)}
                className={`rounded-md px-3 py-2 text-sm font-[family-name:--font-ui] font-medium transition-colors ${
                  allergies.includes(option.id)
                    ? "bg-gi-high text-white"
                    : "border border-border text-text hover:bg-black/5"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {allergies.length === 0 && (
            <p className="text-xs text-muted mt-2 font-[family-name:--font-ui]">No allergies selected</p>
          )}
        </div>
      </div>

      {/* Reminders */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="section-label">Reminders</div>
          {nudgeSaved && (
            <span className="text-xs text-accent font-[family-name:--font-ui] font-medium animate-pulse">Saved</span>
          )}
        </div>
        <p className="text-sm text-muted mb-4">
          Get timely nudges to help you make better food choices throughout the day.
        </p>

        <div className="space-y-4">
          {/* Master toggle */}
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => updateNudgePref("enabled", !nudgePrefs.enabled)}
          >
            <div>
              <div className="text-sm text-text font-[family-name:--font-ui] font-medium">Enable reminders</div>
              <div className="text-xs text-muted font-[family-name:--font-ui]">Show in-app nudges at smart times</div>
            </div>
            <div
              className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${
                nudgePrefs.enabled ? "bg-accent" : "bg-muted/30"
              }`}
            >
              <div
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
                  nudgePrefs.enabled ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </div>
          </div>

          {/* Afternoon slump toggle */}
          <div
            className={`flex items-center justify-between cursor-pointer ${!nudgePrefs.enabled ? "opacity-50 pointer-events-none" : ""}`}
            onClick={() => nudgePrefs.enabled && updateNudgePref("afternoonSlump", !nudgePrefs.afternoonSlump)}
          >
            <div>
              <div className="text-sm text-text font-[family-name:--font-ui] font-medium">Afternoon energy boost</div>
              <div className="text-xs text-muted font-[family-name:--font-ui]">2-4pm snack suggestions to beat the slump</div>
            </div>
            <div
              className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${
                nudgePrefs.enabled && nudgePrefs.afternoonSlump ? "bg-accent" : "bg-muted/30"
              }`}
            >
              <div
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
                  nudgePrefs.afternoonSlump ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </div>
          </div>

          {/* Meal reminder toggle */}
          <div
            className={`flex items-center justify-between cursor-pointer ${!nudgePrefs.enabled ? "opacity-50 pointer-events-none" : ""}`}
            onClick={() => nudgePrefs.enabled && updateNudgePref("mealReminder", !nudgePrefs.mealReminder)}
          >
            <div>
              <div className="text-sm text-text font-[family-name:--font-ui] font-medium">Meal planning tips</div>
              <div className="text-xs text-muted font-[family-name:--font-ui]">Helpful reminders around mealtimes</div>
            </div>
            <div
              className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${
                nudgePrefs.enabled && nudgePrefs.mealReminder ? "bg-accent" : "bg-muted/30"
              }`}
            >
              <div
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
                  nudgePrefs.mealReminder ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* GL Daily Target */}
      <div className="mb-8">
        <div className="section-label">Energy Budget</div>
        <p className="text-sm text-muted mb-4">
          Set your daily Glycemic Load target. A low-GL diet is under 80, moderate is 80–120.
        </p>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={40}
            max={160}
            step={10}
            value={glDailyTarget}
            onChange={(e) => {
              const val = Number(e.target.value);
              setGlDailyTarget(val);
              setGLTarget(val);
            }}
            className="flex-1 accent-accent"
          />
          <div className="flex items-center gap-1 min-w-[4rem]">
            <span className="text-lg font-semibold text-text font-[family-name:--font-ui]">{glDailyTarget}</span>
            <span className="text-xs text-muted font-[family-name:--font-ui]">GL</span>
          </div>
        </div>
        <div className="flex justify-between text-[11px] text-muted font-[family-name:--font-ui] mt-1">
          <span>40 (strict)</span>
          <span>80 (low)</span>
          <span>120 (moderate)</span>
          <span>160</span>
        </div>
      </div>
    </>
  );
}
